import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import { Button, Form, InputGroup, FormControl } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";

function Clinics() {
    const [clinics, setClinics] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState([]);

    const navigate = useNavigate();
    const accesstoken = localStorage.getItem("token");

    useEffect(() => {
        getAllClinics();
    }, []);

    const getAllClinics = () => {
        axios
            .get(baseurladdress + "clinic/findallactiveclinics", {
                headers: { Authorization: `Bearer ${accesstoken}` },
            })
            .then((res) => {
                if (res.data.status === "Success") {
                    setClinics(res.data.clinics);
                }
            })
            .catch((err) => {
                console.error("Error fetching clinics", err);
            });
    };

    function deleteClinic(clinic_id) {
        Swal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(baseurladdress + `clinic/deleteclinic/${clinic_id}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json; charset=UTF-8",
                        Authorization: `Bearer ${accesstoken}`,
                    },
                })
                    .then((response) => response.json())
                    .then((result) => {
                        if (result.status === "success") {
                            Swal.fire({
                                toast: true,
                                position: "top-end",
                                icon: "success",
                                title: "Deleted Successfully",
                                showConfirmButton: false,
                                timer: 1200,
                                timerProgressBar: true,
                            });
                            getAllClinics();
                        } else if (result.status === "Not Found") {
                            Swal.fire({
                                icon: "error",
                                title: "Not Found",
                                text: "Clinic not found or already deleted.",
                            });
                        } else {
                            Swal.fire({
                                icon: "error",
                                title: "Failed",
                                text: "Unable to delete clinic. Please try again.",
                            });
                        }
                    })
                    .catch((error) => {
                        console.error("Delete clinic error:", error);
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: "Something went wrong while deleting clinic.",
                        });
                    });
            }
        });
    }

    function AddClinic() {
        navigate("/addclinic");
    }

    function editClinic(clinic_id) {
        navigate("/editclinic/" + clinic_id);
    }

    // Define columns for TanStack Table
    const columns = useMemo(
        () => [
            {
                accessorKey: "index",
                header: "Sl.No",
                size: 60,
                cell: ({ row }) => row.index + 1,
            },
            {
                accessorKey: "clinic_name",
                header: "Name",
                size: 150,
            },
            {
                accessorKey: "clinic_code",
                header: "Code",
                size: 100,
            },
            {
                accessorKey: "clinic_contact",
                header: "Contact",
                size: 120,
            },
            {
                accessorKey: "clinic_address",
                header: "Address",
                size: 200,
            },
            {
                accessorKey: "district_name",
                header: "District",
                size: 120,
            },
            {
                accessorKey: "staff_name",
                header: "Served By",
                size: 150,
            },
            {
                accessorKey: "actions",
                header: "Actions",
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <Icon.PencilSquare
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => editClinic(row.original.clinic_id)}
                        />
                        <Icon.Trash
                            className="text-danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => deleteClinic(row.original.clinic_id)}
                        />
                    </div>
                ),
                size: 80,
            },
        ],
        []
    );

    // Initialize TanStack Table
    const table = useReactTable({
        data: clinics,
        columns,
        state: {
            globalFilter,
            sorting,
        },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    return (
        <div className="content-wrap">
            <div className="main">
                <div className="container-fluid">
                    <section id="main-content">
                        <div className="card">
                            <div
                                className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3"
                            >
                                <h5 className="mb-0">Clinics</h5>
                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => {
                                        AddClinic();
                                    }}
                                >
                                    <Icon.Plus size={16} className="me-1" />
                                    Add 
                                </Button>
                            </div>

                            <div className="card-body">
                                {/* Compact Search and Controls Bar */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted small">Show</span>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "70px" }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => {
                                                table.setPageSize(Number(e.target.value));
                                            }}
                                        >
                                            {[5, 10, 20, 50].map((pageSize) => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <span className="text-muted small">entries</span>
                                    </div>

                                    <InputGroup style={{ width: "250px" }}>
                                        <InputGroup.Text>
                                            <Icon.Search size={14} />
                                        </InputGroup.Text>
                                        <FormControl
                                            size="sm"
                                            placeholder="Search..."
                                            value={globalFilter ?? ""}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </InputGroup>
                                </div>

                                {/* Table */}
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm align-middle">
                                        <thead className="table-light">
                                            {table.getHeaderGroups().map((headerGroup) => (
                                                <tr key={headerGroup.id}>
                                                    {headerGroup.headers.map((header) => (
                                                        <th
                                                            key={header.id}
                                                            style={{ width: header.getSize() }}
                                                            className="text-nowrap"
                                                        >
                                                            {header.isPlaceholder ? null : (
                                                                <div
                                                                    {...{
                                                                        className: header.column.getCanSort()
                                                                            ? "cursor-pointer select-none d-flex align-items-center"
                                                                            : "",
                                                                        onClick: header.column.getToggleSortingHandler(),
                                                                    }}
                                                                >
                                                                    {flexRender(
                                                                        header.column.columnDef.header,
                                                                        header.getContext()
                                                                    )}
                                                                    {header.column.getCanSort() && (
                                                                        <span className="ms-1">
                                                                            {{
                                                                                asc: <Icon.ChevronUp size={12} />,
                                                                                desc: <Icon.ChevronDown size={12} />,
                                                                            }[header.column.getIsSorted()] ?? (
                                                                                    <Icon.ChevronExpand size={12} className="text-muted" />
                                                                                )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                        </thead>
                                        <tbody>
                                            {table.getRowModel().rows.length > 0 ? (
                                                table.getRowModel().rows.map((row) => (
                                                    <tr key={row.id}>
                                                        {row.getVisibleCells().map((cell) => (
                                                            <td key={cell.id} >
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={columns.length} className="text-center text-muted py-4">
                                                        No clinics found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Compact Pagination */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted small">
                                        Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
                                        {Math.min(
                                            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                            table.getFilteredRowModel().rows.length
                                        )}{" "}
                                        of {table.getFilteredRowModel().rows.length} entries
                                    </div>

                                    <div className="btn-group btn-group-sm">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            <Icon.ChevronDoubleLeft size={14} />
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            <Icon.ChevronLeft size={14} />
                                        </Button>
                                        <Button variant="outline-secondary" disabled>
                                            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            <Icon.ChevronRight size={14} />
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            <Icon.ChevronDoubleRight size={14} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Clinics;